import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';  // Import Firestore
import { doc, getDoc } from 'firebase/firestore';
import {
  Box,
  CssBaseline,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  useTheme,
  useMediaQuery,
  Container,
  Grid,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  LocalShipping as ShipmentsIcon,
  People as SuppliersIcon,
  Settings as SettingsIcon,
  Assignment as PickupsIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import logo from '../assets/ecosphere-logo.png';

const drawerWidth = 280;

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);  // ✅ Add state for user role
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchUserRole = async () => {
      const user = auth.currentUser;
      if (user) {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserRole(userSnap.data().role);  // ✅ Set the user role from Firestore
        }
      }
    };

    fetchUserRole();
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // ✅ Dynamically set menu based on user role
  const menuItems = userRole === "admin" || "superadmin"
    ? [
        { text: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
        { text: "Loads In", icon: <ShipmentsIcon />, path: "/shipments" },
        { text: "Material Composite", icon: <ShipmentsIcon />, path: "/material-composites" },
        { text: "Suppliers", icon: <SuppliersIcon />, path: "/suppliers" },
        { text: "Pickups", icon: <PickupsIcon />, path: "/pickups" },
        { text: "Settings", icon: <SettingsIcon />, path: "/settings" },
      ]
    : [
        { text: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
        { text: "Pickups", icon: <PickupsIcon />, path: "/pickups" },
        { text: "Settings", icon: <SettingsIcon />, path: "/settings" },
      ];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ justifyContent: 'center', p: 2 }}>
        <img
          src={logo}
          alt="Ecosphere Logo"
          style={{ width: 150, height: 'auto' }}
        />
      </Toolbar>
      <List sx={{ flex: 1, px: 1 }}>
        {menuItems.map((item) => (
          <motion.div
            key={item.text}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: '8px',
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === item.path ? 'white' : 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          </motion.div>
        ))}
      </List>
      <Box sx={{ p: 2 }}>
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <ListItemButton
            onClick={() => navigate('/shipments/new')}
            sx={{
              borderRadius: '8px',
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'white' }}>
              <AddIcon />
            </ListItemIcon>
            <ListItemText primary="New Shipment" />
          </ListItemButton>
        </motion.div>
      </Box>
      <Box sx={{ p: 2 }}>
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
          <ListItemButton
            onClick={() => navigate('/material-composites/new')}
            sx={{
              borderRadius: '8px',
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'white' }}>
              <AddIcon />
            </ListItemIcon>
            <ListItemText primary="New Material Composites" />
          </ListItemButton>
        </motion.div>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
        ) : (
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
            open
          >
            {drawer}
          </Drawer>
        )}
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: 'background.default',
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {children}
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;
